import { Directive, ElementRef, Input, OnDestroy } from '@angular/core';
import { TourStepsService } from '../services/tour-steps.service';
import { TourStep } from '../models/tour-step';

// Org-chart node cards are drawn by BALKAN OrgChart.js, not by an Angular template, so
// [TourStep] cannot be placed on one. Query the first rendered card and register its step
// directly, the way ChartHeaderTourStepDirective does for chart-header.
@Directive({ selector: '[orgchartNodeTourStep]' })
export class OrgchartNodeTourStepDirective implements OnDestroy {
  // BALKAN renders each card as real HTML inside a <foreignObject> (reports offsetHeight,
  // which MainTourComponent's visibility gate needs). Anchored on the card's own id
  // (node-${id}) rather than foreignObject > div, which depends on sibling render order.
  private static readonly SELECTOR = 'div[id^="node-"]';
  // Deadline is inherent to MainTourComponent's one-shot 2000 ms read, not a tunable budget.
  // If no card appears within it, the final attempt falls back to the host element.
  private static readonly RETRY_MS = 100;
  private static readonly MAX_ATTEMPTS = 18;

  private step: TourStep;
  private pending: ReturnType<typeof setTimeout> | null = null;

  @Input('orgchartNodeTourStep') set stepData(step: TourStep) {
    this.step = step;
    this.clearPending();
    if (step) this.register();
  }

  constructor(
    private host: ElementRef<HTMLElement>,
    private tourSteps: TourStepsService
  ) {}

  ngOnDestroy() {
    this.clearPending();
  }

  private clearPending() {
    if (this.pending) clearTimeout(this.pending);
    this.pending = null;
  }

  private register(attempt = 0) {
    this.pending = null;
    const card = this.host.nativeElement.querySelector<HTMLElement>(
      OrgchartNodeTourStepDirective.SELECTOR
    );
    const isFinalAttempt = attempt >= OrgchartNodeTourStepDirective.MAX_ATTEMPTS;
    if (!card && !isFinalAttempt) {
      this.pending = setTimeout(
        () => this.register(attempt + 1),
        OrgchartNodeTourStepDirective.RETRY_MS
      );
      return;
    }
    // No card rendered in time (slow load): fall back to the chart container itself so the
    // step still joins the tour, per the design spec's documented fallback.
    const element = card ?? this.host.nativeElement;
    const on = this.step.on ?? 'bottom';
    this.tourSteps.addStep(this.step.tourKey, {
      order: this.step.order,
      isRows: this.step.isRows,
      options: {
        title: this.step.title,
        text: this.step.text,
        attachTo: { element, on },
        classes: on.includes('left') ? '-mx-5' : 'mx-5',
      },
    });
  }
}
